import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TechLayout from '../../components/tech/TechLayout';
import {
  getWorkOrder,
  getWorkOrderParts,
  getPhotos,
  techUpdateWorkOrder,
  addNote,
} from '../../api/client';

export default function TechCompleteJob() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [usedParts, setUsedParts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [form, setForm] = useState({
    completion_notes: '',
    labor_cost: '',
    diagnosis_done: false,
    photos_uploaded: false,
    customer_informed: false,
    parts_recorded: false,
  });

  const load = async () => {
    try {
      setLoading(true);

      const [jobRes, partsRes, photosRes] = await Promise.all([
        getWorkOrder(id),
        getWorkOrderParts(id).catch(() => ({ data: [] })),
        getPhotos(id).catch(() => ({ data: [] })),
      ]);

      const wo = jobRes.data;
      const parts = partsRes.data || [];
      const imgs = photosRes.data || [];

      setJob(wo);
      setUsedParts(parts);
      setPhotos(imgs);

      setForm({
        completion_notes: wo.completion_notes || wo.resolution || '',
        labor_cost: wo.labor_cost || '',
        diagnosis_done: !!wo.diagnosis,
        photos_uploaded: imgs.length > 0,
        customer_informed: false,
        parts_recorded: parts.length > 0,
      });
    } catch (err) {
      console.error('load complete page failed:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleComplete = async () => {
    if (!form.completion_notes.trim()) {
      setMessage('Please fill in completion notes');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      setSaving(true);

      // 只發後端 schema 允許的字段
      const payload = {
        status: 'completed',
        resolution: form.completion_notes,
        technician_notes: form.completion_notes,
        labor_cost: form.labor_cost === '' ? job.labor_cost : Number(form.labor_cost),
      };

      const res = await techUpdateWorkOrder(id, payload);
      setJob(res.data);

      // 同時保存為工單 Note，管理端可在 Notes 裏看到
      await addNote(id, {
        content: `[Completion Notes] ${form.completion_notes}`,
      }).catch(() => {});

      setMessage('Job completed successfully!');
      setTimeout(() => navigate(`/tech/jobs/${id}`), 1500);
    } catch (err) {
      console.error('complete job failed:', err.response?.data || err);
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === 'string' ? detail : 'Failed to complete job, please try again');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TechLayout title="Complete Job">
      <button
        onClick={() => navigate(`/tech/jobs/${id}`)}
        style={{
          marginBottom: 14,
          border: 'none',
          background: '#F3F4F6',
          padding: '8px 12px',
          borderRadius: 8,
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        ← Back
      </button>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading...</div>
      ) : (
        <>
          <Section title="Job Summary">
            <Info label="Job" value={job?.job_number || `WO-${job?.id}`} />
            <Info label="Customer" value={job?.customer_name} />
            <Info label="Status" value={job?.status} />
            <Info label="Parts Used" value={usedParts.length} />
            <Info label="Photos Uploaded" value={photos.length} />
          </Section>

          <Section title="Completion Notes">
            <textarea
              value={form.completion_notes}
              onChange={(e) => setForm({ ...form, completion_notes: e.target.value })}
              placeholder="Describe what was repaired / completed..."
              style={textareaStyle}
            />

            <div style={{ marginTop: 12 }}>
              <Field label="Labor Cost ($)">
                <input
                  type="number"
                  step="0.01"
                  value={form.labor_cost}
                  onChange={(e) => setForm({ ...form, labor_cost: e.target.value })}
                  style={inputStyle}
                />
              </Field>
            </div>
          </Section>

          <Section title="Completion Checklist">
            <CheckRow
              label="Diagnosis completed"
              checked={form.diagnosis_done}
              onChange={(checked) => setForm({ ...form, diagnosis_done: checked })}
            />

            <CheckRow
              label="Photos uploaded"
              checked={form.photos_uploaded}
              onChange={(checked) => setForm({ ...form, photos_uploaded: checked })}
            />

            <CheckRow
              label="Customer informed"
              checked={form.customer_informed}
              onChange={(checked) => setForm({ ...form, customer_informed: checked })}
            />

            <CheckRow
              label="Parts recorded or not needed"
              checked={form.parts_recorded}
              onChange={(checked) => setForm({ ...form, parts_recorded: checked })}
            />
          </Section>

          <button
            onClick={handleComplete}
            disabled={saving}
            style={{
              width: '100%',
              border: 'none',
              background: '#059669',
              color: '#fff',
              borderRadius: 10,
              padding: '14px 16px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              marginTop: 10,
            }}
          >
            {saving ? 'Completing...' : 'Complete Job'}
          </button>

          {message && (
            <div
              style={{
                marginTop: 10,
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 700,
                color: message.toLowerCase().includes('success') ? '#059669' : '#DC2626',
              }}
            >
              {message}
            </div>
          )}
        </>
      )}
    </TechLayout>
  );
}

function Section({ title, children }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        padding: 16,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: '#374151',
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: '#6B7280',
          fontWeight: 700,
          textTransform: 'uppercase',
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 11,
          color: '#6B7280',
          fontWeight: 700,
          textTransform: 'uppercase',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#111827' }}>{value || '—'}</div>
    </div>
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 0',
        borderBottom: '1px solid #F3F4F6',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16 }}
      />
      <span style={{ fontSize: 14, color: '#111827' }}>{label}</span>
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #D1D5DB',
  fontSize: 14,
  boxSizing: 'border-box',
};

const textareaStyle = {
  width: '100%',
  minHeight: 140,
  borderRadius: 8,
  border: '1px solid #D1D5DB',
  padding: 10,
  fontSize: 14,
  resize: 'vertical',
  boxSizing: 'border-box',
};